// Ultimate Card Game (UCG) - Command Processor
//
// One function per command type, each doing exactly what the old inline
// click-handler logic in main.js/hud.js used to do (same engine calls, same
// notifications/sounds gated to whichever team is the LOCAL human -
// game.myTeam, 'western' by default/single-player, but 'eastern' for a
// player who joined rather than created an online match - team labels
// carry real simulation meaning (western spawns/attacks from the left,
// eastern from the right) and are never relabeled per-client, unlike an
// earlier version of this file assumed). Centralizing it here means a
// click handler, a hotkey, and a CLIENT_CMD received over the WebSocket
// (server/matchRoom.js) all produce the same command object and get
// identical treatment.
//
// The bot AI is intentionally NOT routed through this queue: it only ever
// plays as 'eastern', which never appears in an online match, so there's
// nothing to gain from the extra indirection. It still calls the same
// underlying CardSystem/Economy methods these handlers call, so there's no
// duplicated game logic - just duplicated wiring, which is fine here.
//
// Neustart "Marine Null": CO_POWER_MINERALS/CO_POWER_PLASMA and
// UPGRADE_UNIT are gone along with the resources and the on-field unit
// upgrade panel they belonged to. DISCARD and FREE_SWAP are gone because
// cards can no longer be discarded at all. DRAW is new - see
// js/engine/cardSystem.js's drawCard()/PENDING_CARD and
// NEUSTART_PLAN.html Phase 3 for why slot-claim and plasma-spend happen
// here (deterministically, every client, every time) while only the card's
// *identity* is allowed to resolve late in an online match.

import { CARDS_DATA, getCardByName } from '../data/cards.js';

// Shared by the player's NUKE command and the bot's own superweapon logic
// (js/engine/ai.js) so the actual detonation effect - crater/obsidian glass,
// board wipe, notification - exists in exactly one place.
export function triggerNuke(game, team, x, y) {
  const { battlefield, particles, audio } = game;
  battlefield?.stats?.recordSuperweapon?.(team);
  audio?.playNukeAlarm?.();
  particles?.triggerNuclearStrike?.(x, y, () => {
    audio?.playExplosion?.('massive');
    battlefield?.addRadioactiveCrater?.(x, y);
    if (battlefield?.sweepSuperweapon) {
      battlefield.sweepSuperweapon(particles, 'NUKE');
    } else {
      const units = [...(battlefield?.units || [])];
      for (const u of units) {
        // 'Energy' bypasses Deflection Rating (see Unit.takeDamage) - a nuke
        // must guarantee a kill on every unit and structure.
        // Pass false to showDamageText to suppress 99999 floating hit numbers.
        u.takeDamage(99999, particles, 'Energy', false, false, null, false, { isSuperweapon: true, superType: 'NUKE' });
      }
      if (battlefield?.goldenStatues) {
        const statues = [...battlefield.goldenStatues];
        for (const s of statues) {
          if (s.isStickman) {
            s.vaporizeObstacle?.(particles, 'NUKE');
          }
        }
      }
    }
  });
}

export function triggerBioPlasma(game, team, x, y) {
  const { battlefield, particles, audio } = game;
  battlefield?.stats?.recordSuperweapon?.(team);
  // Sul variant has no siren alarm; hush is played, BGM ducked, and incision/wipe triggered.
  audio?.playBioPlasmaHush?.();
  audio?.duckBGM?.(0.15, 1.95);
  particles?.triggerBioPlasmaStrike?.(x, y, () => {
    audio?.playBioPlasmaIncision?.();
    battlefield?.addObsidianGlassField?.(x, y);
  }, () => {
    if (battlefield?.sweepSuperweapon) {
      battlefield.sweepSuperweapon(particles, 'BIO_PLASMA');
    } else {
      const units = [...(battlefield?.units || [])];
      for (const u of units) {
        u.takeDamage(99999, particles, 'Energy', false, false, null, false, { isSuperweapon: true, superType: 'BIO_PLASMA' });
      }
      if (battlefield?.goldenStatues) {
        const statues = [...battlefield.goldenStatues];
        for (const s of statues) {
          if (s.isStickman) {
            s.vaporizeObstacle?.(particles, 'BIO_PLASMA');
          }
        }
      }
    }
  }, battlefield);
}

export function triggerSuperweapon(game, team, x, y) {
  const race = game.battlefield?.bases?.[team]?.race;
  if (race === 'Sul') {
    return triggerBioPlasma(game, team, x, y);
  }
  return triggerNuke(game, team, x, y);
}

export class CommandProcessor {
  constructor(game) {
    this.game = game;
    this.appliedCommandIds = new Set();
  }

  isCommandApplied(commandId) {
    if (!commandId) return false;
    return this.appliedCommandIds.has(commandId);
  }

  apply(command) {
    if (!command || !command.type) return false;

    // Idempotency: prevent any command from executing twice across P2P / Turn boundaries
    if (command.id) {
      if (this.appliedCommandIds.has(command.id)) {
        return false;
      }
      this.appliedCommandIds.add(command.id);
      if (this.appliedCommandIds.size > 2000) {
        const first = this.appliedCommandIds.values().next().value;
        this.appliedCommandIds.delete(first);
      }
    }

    switch (command.type) {
      case 'PLAY': return this._play(command);
      case 'DRAW': return this._draw(command);
      case 'NUKE': return this._nuke(command);
      case 'MELD': return this._meld(command);
      case 'TAUNT': return this._taunt(command);
      default:
        console.warn('[UCG] Unknown command type:', command?.type);
        return false;
    }
  }

  // `cardName` used to be trusted here (a client-claimed identity would
  // overwrite whatever was actually in the hand slot) - that was safe only
  // in the old peer-lockstep model, where the far end was a second fully-
  // simulating peer confirming its own already-computed draw, never an
  // untrusted player. On the authoritative server (server/gameSimulation.js)
  // the far end is just a player's browser, so trusting it let a modified
  // client play any named card regardless of what it actually held - see
  // docs/SCALING_AUDIT_KRITERIENKATALOG.md K-1.2. The played card now comes
  // exclusively from the server's own authoritative hand.
  _play({ team, playerRole, slot, targetUnitId, edition, customAuraHex }) {
    const g = this.game;
    const roleOrTeam = playerRole || team;
    const effectiveTeam = (roleOrTeam === 'w1' || roleOrTeam === 'w2' || roleOrTeam === 'western') ? 'western' : 'eastern';

    // Campaign-exclusive
    if (g.isCampaignMission && g._campaignActivationUnit) {
      const card = (g.cardSystem.hands[roleOrTeam] || g.cardSystem.hands[team])?.[slot];
      if (!card) return false;
      if (g.cardSystem.hands[roleOrTeam]) g.cardSystem.hands[roleOrTeam][slot] = null;
      else if (g.cardSystem.hands[team]) g.cardSystem.hands[team][slot] = null;
      g._campaignActivationUnit.isCinematic = false;
      g._campaignActivationUnit = null;
      g.campaignCombatArmed = true;
      g.battlefield?.stats?.recordCardPlayed(effectiveTeam);
      g.hud?.setCampaignCinematicMode?.(false);
      if (g._campaignFullHudCombat) {
        g.hud?.setVisible?.(true);
      }
      return true;
    }

    // Reject play if slot is currently fusing in a pending triplet melt
    if (g.cardSystem?.pendingMelts?.[roleOrTeam]?.slots?.includes(slot) || g.cardSystem?.pendingMelts?.[team]?.slots?.includes(slot)) {
      return false;
    }

    const hand = g.cardSystem?.hands?.[roleOrTeam] || g.cardSystem?.hands?.[team];
    const card = hand?.[slot];
    if (!card || card.hidden) return false;

    const isOpponent = effectiveTeam !== (g.myTeam || 'western');
    const playedCardName = card.name;
    const played = g.cardSystem.playCard(roleOrTeam, slot, targetUnitId, { edition, customAuraHex });

    // Special-edition reveal (Gold Edition etc.)
    if (played && edition && isOpponent) {
      g.audio?.playSpecialCardAlert?.();
      g.specialCardToast?.show({ team: effectiveTeam, cardName: playedCardName, edition });
      g.hud?.pulseSpecialBadge?.(effectiveTeam);
    }

    return played;
  }

  // Slot claim + plasma spend happen here, unconditionally and
  // identically on every client. What card it turns out to be is resolved
  // inside drawCard() - synchronously in single-player, or via
  // RemoteDrawBuffer (falling back to PENDING_CARD on a buffer underrun)
  // in an online match - and update() below retries every tick until the
  // buffer catches up.
  _draw({ team, playerRole, slot }) {
    const g = this.game;
    const cs = g.cardSystem;
    const eco = g.economy;
    const roleOrTeam = playerRole || team;
    const hand = cs.hands[roleOrTeam] || cs.hands[team];
    if (!hand || hand[slot] !== null) return false; // slot must be empty

    const drawCost = cs.getEffectiveDrawCost ? cs.getEffectiveDrawCost(roleOrTeam) : cs.drawCost;
    if (drawCost > 0 && !eco.spendPlasma(roleOrTeam, drawCost)) {
      cs.lastDrawErrorSlot = { team: roleOrTeam, slot };
      cs.drawErrorSeq = (cs.drawErrorSeq || 0) + 1;
      if (roleOrTeam === (g.myRole || g.myTeam || 'western')) {
        g.audio?.playError?.();
      }
      return false;
    }

    return cs.drawCard(roleOrTeam, slot);
  }

  _nuke({ team, playerRole, x, y }) {
    const g = this.game;
    const effectiveTeam = (playerRole === 'w1' || playerRole === 'w2' || playerRole === 'western' || team === 'western') ? 'western' : 'eastern';
    const roleOrTeam = playerRole || team || effectiveTeam;

    // 2v2: Check 30s lockout for team
    if (g.superweaponLockout && g.superweaponLockout[effectiveTeam] > 0) {
      return false;
    }

    const charges = g.superweaponCharges || g.nukesAvailable;
    if (!charges || charges[roleOrTeam] <= 0) return false;

    charges[roleOrTeam] = 0;
    if (charges[effectiveTeam] !== undefined && !g.is2v2) {
      charges[effectiveTeam] = 0;
    }
    if (g.superweaponCharges && g.superweaponCharges[roleOrTeam] !== undefined) {
      g.superweaponCharges[roleOrTeam] = 0;
    }
    if (g.nukesAvailable && g.nukesAvailable[roleOrTeam] !== undefined) {
      g.nukesAvailable[roleOrTeam] = 0;
    }

    // Set 30s team lockout
    if (g.superweaponLockout) {
      g.superweaponLockout[effectiveTeam] = 30.0;
    }

    if (roleOrTeam === (g.myRole || g.myTeam || 'western')) {
      (g.updateSuperButtonState || g.updateNukeButtonState)?.call(g);
    }
    triggerSuperweapon(g, effectiveTeam, x, y);
    return true;
  }

  // Never trusted the client's claimed `meltCardName` for the result, nor
  // even that the 3 slots formed a legal triplet at all - a modified client
  // could name any 3 owned slots and receive the game's most powerful Hyper
  // card for free (docs/SCALING_AUDIT_KRITERIENKATALOG.md K-1.2). The
  // result is now always recomputed here from the 3 actual cards, mirroring
  // CardSystem.checkDrilling()/checkSymbiosis() (js/engine/cardSystem.js) -
  // duplicated rather than shared because those two run as a background
  // scanner over the whole hand (finding a triplet), while this validates
  // one specific claimed triplet; on the live server this command path
  // isn't even how legitimate melds happen (checkDrilling/checkSymbiosis
  // already fuse automatically, tick by tick - see CardSystem.update()) -
  // it only exists for the old peer-relay model - so this only ever needs
  // to be exactly as permissive as those two, never more.
  _meld({ team, slots }) {
    const g = this.game;
    const cs = g.cardSystem;
    const hand = cs?.hands?.[team];
    if (!hand || !Array.isArray(slots) || slots.length !== 3) return false;
    const [i1, i2, i3] = slots;
    const a = hand[i1], b = hand[i2], c = hand[i3];
    if (!a || !b || !c || a.pending || b.pending || c.pending || a.hidden || b.hidden || c.hidden) return false;

    let meltCard = null;

    // Human "Drilling": 3 identical non-Sul, non-Melt cards -> "Hyper <name>".
    if (a.race !== 'Sul' && b.race !== 'Sul' && c.race !== 'Sul' &&
        a.type !== 'Melt' && b.type !== 'Melt' && c.type !== 'Melt' &&
        a.name === b.name && b.name === c.name) {
      const candidate = getCardByName(`hyper ${a.name.toLowerCase()}`);
      if (candidate && candidate.type === 'Melt') meltCard = candidate;
    }

    // Sul "Symbiosis": 3 identical same-caste Sul cards -> random next-caste unit.
    if (!meltCard && a.race === 'Sul' && b.race === 'Sul' && c.race === 'Sul' &&
        a.caste && a.caste === b.caste && b.caste === c.caste &&
        a.name === b.name && b.name === c.name) {
      const nextCasteMap = { Loaf: 'Pillar', Pillar: 'Lock', Lock: 'Vulture', Vulture: 'Symbiosis' };
      const targetCaste = nextCasteMap[a.caste];
      if (targetCaste) {
        const matchesCaste = (card) => card.race === 'Sul' &&
          (card.caste === targetCaste || (targetCaste === 'Symbiosis' && (card.type === 'Symbiosis' || card.isSymbiosisResult)));
        const deck = team === 'western' ? (cs.playerDeck || []) : (cs.enemyDeck || []);
        let candidates = deck.filter(matchesCaste);
        if (candidates.length === 0) candidates = CARDS_DATA.filter(matchesCaste);
        if (candidates.length > 0) meltCard = cs.rng.pick(candidates);
      }
    }

    // Pulp Wildcard: 1 Pulp + 2 identical valid cards, OR 2 Pulps + 1 valid card
    let resultSlot = i2;
    if (!meltCard) {
      const trio = [a, b, c];
      const pulps = trio.filter(card => card.name === 'Pulp');
      const nonPulps = trio.filter(card => card.name !== 'Pulp');

      const resolveFusion = (base) => {
        if (!base || base.type === 'Melt' || base.isStructure || base.isSpell) return null;
        if (base.race !== 'Sul') {
          const candidate = getCardByName(`hyper ${base.name.toLowerCase()}`);
          if (candidate && candidate.type === 'Melt') return candidate;
        } else if (base.caste) {
          const nextCasteMap = { Loaf: 'Pillar', Pillar: 'Lock', Lock: 'Vulture', Vulture: 'Symbiosis' };
          const targetCaste = nextCasteMap[base.caste];
          if (targetCaste) {
            const matchesCaste = (card) => card.race === 'Sul' &&
              (card.caste === targetCaste || (targetCaste === 'Symbiosis' && (card.type === 'Symbiosis' || card.isSymbiosisResult)));
            const deck = team === 'western' ? (cs.playerDeck || []) : (cs.enemyDeck || []);
            let candidates = deck.filter(matchesCaste);
            if (candidates.length === 0) candidates = CARDS_DATA.filter(matchesCaste);
            if (candidates.length > 0) return cs.rng ? cs.rng.pick(candidates) : candidates[0];
          }
        }
        return null;
      };

      if (pulps.length === 1 && nonPulps.length === 2 && nonPulps[0].name === nonPulps[1].name) {
        meltCard = resolveFusion(nonPulps[0]);
        // Set result slot to first non-pulp slot
        resultSlot = a.name !== 'Pulp' ? i1 : i2;
      } else if (pulps.length === 2 && nonPulps.length === 1) {
        meltCard = resolveFusion(nonPulps[0]);
        // Set result slot to the single non-pulp slot
        resultSlot = a.name !== 'Pulp' ? i1 : (b.name !== 'Pulp' ? i2 : i3);
      }
    }

    if (!meltCard) return false;

    // Result lands in resultSlot - all other participating slots are cleared
    for (const s of [i1, i2, i3]) {
      if (s !== resultSlot) hand[s] = null;
    }
    const nextId = cs.nextInstanceId ? cs.nextInstanceId() : `card_meld_${team}_${resultSlot}`;
    hand[resultSlot] = { ...meltCard, slot: resultSlot, instanceId: nextId };
    return true;
  }

  _taunt({ team, tauntId }) {
    if (!team || !tauntId) return false;
    this.game.tauntManager?.showTauntBubble?.(team, tauntId);
    return true;
  }
}
